import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { Carousel } from 'primeng/carousel';
import { finalize } from 'rxjs/operators';
import { Utils } from 'src/app/services/utils/utils.service';
import { environment } from '../../../environments/environment';
import { ContentText } from '../../models/content-text';
import { VehicleDetail } from '../../models/vehicle-detail';
import { ContentDataService } from '../../services/content/content-data.service';
import { SAPDataService } from '../../services/sap/sap-data.service';
import { SharedStateService } from '../../services/shared-state/shared-state.service';

@Component({
    selector: 'app-vehicle-details-just-browsing',
    templateUrl: './vehicle-details-just-browsing.component.' + environment.brand + '.html',
    styleUrls: ['./vehicle-details-just-browsing.component.' + environment.brand + '.css'],
    providers: [ContentDataService, SAPDataService]
})
export class VehicleDetailsJustBrowsingComponent implements AfterViewInit {
    // dynamic text loaded from db
    contentTexts: ContentText[] = [];
    contentImages = [];
    modelOptions = [];
    selectedModel = '';
    modelsLoaded = false;
    modelYearOptions = [];
    selectedModelYear = '';

    // carousel vars
    @ViewChild('carousel') carousel: Carousel;
    cars: string[] = [];
    carouselPageIndex = -1;
    numVisible = 3;
    numScroll = 3;
    responsiveOptions;

    currentOdometer: number;
    odometerValidationMessage = '';
    get odometerValid(): boolean {
        return this.currentOdometer > 0 && this.odometerValidationMessage == '';
    }

    planTypeOptions = [];
    selectedPlanType = '';
    yearsOptions = [];
    selectedYears = '';
    distanceOptions = [];
    selectedDistance = '';

    get isPlanTypeUnknown(): boolean {
        return this.selectedPlanType != 'Maintenance' && this.selectedPlanType != 'Service';
    }

    get canContinue(): boolean {
        if (!this.odometerValid) {
            return false;
        }

        if (this.isPlanTypeUnknown) {
            return true; // no need to select years and distance
        } else {
            return +this.selectedDistance > 0;
        }
    }

    constructor(private sharedStateService: SharedStateService,
        private contentDataService: ContentDataService,
        private sapDataService: SAPDataService,
        private utils: Utils) {
        // set carousel responsive options            
        this.responsiveOptions = [
            { breakpoint: '560px', numVisible: 1, numScroll: 1 },
            { breakpoint: '768px', numVisible: 2, numScroll: 2 },
            { breakpoint: '1024px', numVisible: 3, numScroll: 3 }
        ];
    }

    ngAfterViewInit(): void {
        this.getContentImages();

        // get dynamic text
        const pageName = 'Just Browsing';
        this.contentTexts = this.sharedStateService.contentTexts.filter(x => x.pageName == pageName);
        if (this.contentTexts.length == 0) {
            this.sharedStateService.onContentTextLoaded.subscribe(() => {
                this.contentTexts = this.sharedStateService.contentTexts.filter(x => x.pageName == pageName);
            });
        }
    }

    getContentImages(): void {
        this.sharedStateService.toggleLoader(true);
        this.contentDataService.getContentImages('Just-Browsing')
            .pipe(finalize(() => {
                this.sharedStateService.toggleLoader(false);
                this.loadModelOptions();
            }))
            .subscribe(results => {
                this.contentImages = results;
            }, error => {
                this.utils.log('getContentImages', error);
            });
    }

    loadModelOptions(): void {
        this.modelYearOptions = [];
        this.selectedModelYear = '';
        this.planTypeOptions = [];
        this.selectedPlanType = '';
        this.yearsOptions = [];
        this.selectedYears = '';
        this.distanceOptions = [];
        this.selectedDistance = '';

        this.sharedStateService.toggleLoader(true);
        this.sapDataService.getModelsJustBrowsing()
            .pipe(finalize(() => {
                this.sharedStateService.toggleLoader(false);
            }))
            .subscribe(models => {
                //this.utils.log('models', models);
                if (models && models.length > 0) {
                    // populate dropdown data
                    this.modelOptions.push({ value: 'temp', label: 'Please select a model', segment: '', brand: '' });
                    models.forEach(model => {
                        this.modelOptions.push({ value: model.model, label: model.model, segment: model.segment, brand: model.brand });
                        this.cars.push(model.model);
                    });
                    // set selected option
                    if (this.sharedStateService.selectedVehicleDetail?.modelDescription) {
                        //this.utils.log('setting model from shared state');
                        this.selectedModel = this.sharedStateService.selectedVehicleDetail.modelDescription;
                        this.onModelSelected('shared_state');
                    }
                    else {
                        this.selectedModel = this.modelOptions[0].value;
                        this.carouselPageIndex = 0;
                    }
                }
            }, error => {
                this.utils.log('loadModelOptions', error);
                this.sharedStateService.showDialog({
                    isVisible: true,
                    header: 'Error getting vehicle models',
                    message: 'Please try again or contact us for assistance.'
                });
            });
    }

    onModelSelected(event: any): void {
        //this.utils.log('onModelSelected', event);

        if (event != 'shared_state') {
            // invalidate the selected options
            this.sharedStateService.selectedVehicleDetail = undefined;
        }

        this.showCarouselItem();

        // reset cascading data
        this.modelYearOptions = [];
        this.selectedModelYear = '';
        this.planTypeOptions = [];
        this.selectedPlanType = '';
        this.yearsOptions = [];
        this.selectedYears = '';
        this.distanceOptions = [];
        this.selectedDistance = '';
        this.currentOdometer = undefined;

        if (this.selectedModel == '') {
            return;
        }

        // add temp option
        this.modelYearOptions.push({ value: 'temp', label: 'Please select a year' });

        // add years
        const currentYear = new Date().getFullYear();
        for (let i = currentYear; i > currentYear - 10; i--) {
            this.modelYearOptions.push({ value: i.toString(), label: i.toString() });
        }

        // set the selected option
        if (this.sharedStateService.selectedVehicleDetail?.year) {
            //this.utils.log('setting model year from shared state');
            this.selectedModelYear = this.sharedStateService.selectedVehicleDetail.year
            this.onModelYearSelected();
        }
        else {
            this.selectedModelYear = this.modelYearOptions[0].value.toString();
        }
    }

    showCarouselItem(): void {
        // [0,1] [2,3] [4,5]
        // [0,1,2] [3,4,5] [6,7,8]
        // find the page that the selected model is on
        const itemIndex = this.cars.indexOf(this.selectedModel);
        this.carouselPageIndex = Math.ceil((itemIndex + 1) / this.carousel.numVisible) - 1;
    }

    carouselItemClicked(event: any): void {
        // set dropdown selected item
        this.selectedModel = event;
        this.onModelSelected('carouselItemClicked');
    }

    onCarouselPage(event: any): void {
        this.carouselPageIndex = event.page;
    }

    onModelYearSelected(): void {
        //this.utils.log('onModelYearSelected');

        // reset cascading data
        this.planTypeOptions = [];
        this.selectedPlanType = '';
        this.yearsOptions = [];
        this.selectedYears = '';
        this.distanceOptions = [];
        this.selectedDistance = '';
        this.currentOdometer = undefined;

        this.sharedStateService.toggleLoader(true);
        this.sapDataService.getPlanTypesJustBrowsing()
            .pipe(finalize(() => {
                this.sharedStateService.toggleLoader(false);
            }))
            .subscribe(planTypes => {
                if (planTypes && planTypes.length > 0) {
                    // populate dropdown data
                    this.planTypeOptions.push({ value: 'temp', label: 'Please select a plan type' });
                    planTypes.forEach(planType => {
                        this.planTypeOptions.push({ value: planType, label: planType });
                    });
                    // set selected option
                    if (this.sharedStateService.selectedVehicleDetail?.planType) {
                        //this.utils.log('setting plan type from shared state');
                        this.selectedPlanType = this.sharedStateService.selectedVehicleDetail.planType
                        this.onPlanTypeSelected();
                        // set the odometer value if the user has come back from the next page
                        if (this.sharedStateService.selectedVehicleDetail?.currentOdometer) {
                            this.currentOdometer = this.sharedStateService.selectedVehicleDetail.currentOdometer;
                            this.onCurrentOdometerChanged();
                        }
                    }
                    else {
                        this.selectedPlanType = this.planTypeOptions[0].value.toString();
                    }
                }
            }, error => {
                //this.utils.log('onModelYearSelected', error);
                this.sharedStateService.showDialog({
                    isVisible: true,
                    header: 'Error getting plan types',
                    message: 'Please try again or contact us for assistance.'
                });
            });
    }

    onCurrentOdometerChanged(): void {
        //this.utils.log('onCurrentOdometerChanged', this.currentOdometer);
        if (this.currentOdometer > 0) {
            // only allow integers
            if (this.currentOdometer.toString().includes('.') || this.currentOdometer.toString().includes(',')) {
                this.odometerValidationMessage = 'Please enter a non-decimal value';
                return;
            }
            // the value is valid
            this.odometerValidationMessage = '';
            return;
        } else {
            this.odometerValidationMessage = 'Please enter your current odometer reading';
        }
    }

    onPlanTypeSelected(): void {
        this.utils.log('onPlanTypeSelected', this.selectedPlanType);

        // reset cascading data
        this.yearsOptions = [];
        this.selectedYears = '';
        this.distanceOptions = [];
        this.selectedDistance = '';

        if (this.selectedPlanType == '' || this.selectedPlanType == 'temp') {
            return;
        }

        // validate odometer
        this.onCurrentOdometerChanged();

        // get model for additional info
        const model = this.modelOptions.find(x => x.value == this.selectedModel);

        if (!this.isPlanTypeUnknown) {
            // get the plan years
            this.sharedStateService.toggleLoader(true);
            this.sapDataService.getYearsJustBrowsing(model.brand, model.segment, this.selectedPlanType)
                .pipe(finalize(() => {
                    this.sharedStateService.toggleLoader(false);
                }))
                .subscribe(planYears => {
                    if (planYears && planYears.length > 0) {
                        // populate dropdown data
                        this.yearsOptions.push({ value: 'temp', label: 'Please select the years' });
                        planYears.forEach(year => {
                            this.yearsOptions.push({ value: year, label: year });
                        });
                        // set selected option
                        if (this.sharedStateService.selectedVehicleDetail?.currentPlanYears) {
                            //this.utils.log('setting plan years from shared state');
                            this.selectedYears = this.sharedStateService.selectedVehicleDetail.currentPlanYears.toString();
                            this.onYearsSelected();
                        }
                        else {
                            this.selectedYears = this.yearsOptions[0].value.toString();
                        }
                    }
                }, error => {
                    this.utils.log('onPlanTypeSelected', error);
                    this.sharedStateService.showDialog({
                        isVisible: true,
                        header: 'Error getting plan years',
                        message: 'Please try again or contact us for assistance.'
                    });
                });
        } else {
            // get standard plan info
            this.sharedStateService.toggleLoader(true);
            this.sapDataService.getStandardPlanInfo(model.brand, model.segment)
                .pipe(finalize(() => {
                    this.sharedStateService.toggleLoader(false);
                }))
                .subscribe(result => {
                    this.selectedYears = result.years.toString();
                    this.selectedDistance = result.kilometers.toString();
                }, error => {
                    this.utils.log('onPlanTypeSelected', error);
                    this.sharedStateService.showDialog({
                        isVisible: true,
                        header: 'Error getting standard plan info',
                        message: 'Please try again or contact us for assistance.'
                    });
                });
        }
    }

    onYearsSelected(): void {
        //this.utils.log('onYearsSelected');
        this.distanceOptions = [];
        this.selectedDistance = '';

        if (this.selectedYears == '' || this.selectedYears == 'temp') {
            return;
        }

        // get model for additional info
        const model = this.modelOptions.find(x => x.value == this.selectedModel);

        this.sharedStateService.toggleLoader(true);
        this.sapDataService.getDistanceJustBrowsing(model.brand, model.segment, this.selectedPlanType, +this.selectedYears)
            .pipe(finalize(() => {
                this.sharedStateService.toggleLoader(false);
            }))
            .subscribe(planKMs => {
                if (planKMs && planKMs.length > 0) {
                    this.distanceOptions.push({ value: 'temp', label: 'Please select the distance' });
                    planKMs.forEach(year => {
                        this.distanceOptions.push({ value: year, label: year });
                    });
                    // set selected option
                    if (this.sharedStateService.selectedVehicleDetail?.currentPlanEndKM) {
                        //this.utils.log('setting plan years from shared state');
                        this.selectedDistance = this.sharedStateService.selectedVehicleDetail.currentPlanEndKM.toString();
                    }
                    else {
                        this.selectedDistance = this.distanceOptions[0].value.toString();
                    }
                }
            }, error => {
                this.utils.log('onYearsSelected', error);
                this.sharedStateService.showDialog({
                    isVisible: true,
                    header: 'Error getting plan distance',
                    message: 'Please try again or contact us for assistance.'
                });
            });
    }

    onDistanceSelected(): void {
        this.utils.log('onDistanceSelected');
    }

    onContinue(): void {
        if (this.canContinue) {
            // data for 'select plan'
            const vehicleDetail = new VehicleDetail();
            vehicleDetail.modelDescription = this.selectedModel;
            vehicleDetail.year = this.selectedModelYear;
            vehicleDetail.currentOdometer = this.currentOdometer;
            vehicleDetail.planType = this.selectedPlanType;
            vehicleDetail.currentPlanYears = +this.selectedYears;
            vehicleDetail.currentPlanEndKM = +this.selectedDistance;
            const model = this.modelOptions.find(x => x.value == this.selectedModel);
            vehicleDetail.brandCode = model?.brand;
            vehicleDetail.segment = model?.segment;
            this.sharedStateService.selectedVehicleDetail = vehicleDetail;
            // show 'select plan'
            this.sharedStateService.showSelectPlan();
        }
    }

    onBack(): void {
        this.sharedStateService.showStart();
    }
}
